import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  KeyRound,
  Loader2,
  Mail,
  MessageCircle,
  MessagesSquare,
  Package,
  Power,
  ShoppingBag,
  Store,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useSiteStatus } from "@/context/SiteStatusContext";
import { formatPrice, formatDateTime, ORDER_STATUS, PAYMENT_LABELS } from "@/lib/format";
import { AdminCard, Pill, TableWrap, Td, Th } from "@/pages/admin/ui";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const { resellers, refresh } = useSiteStatus();
  const resellersOn = resellers?.enabled !== false;
  const [togglingResellers, setTogglingResellers] = useState(false);
  const [testingWa, setTestingWa] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);

  const toggleResellers = async () => {
    setTogglingResellers(true);
    try {
      const { data } = await api.put("/admin/reseller-module", { enabled: !resellersOn });
      await refresh();
      toast.success(data.enabled ? "Module revendeurs ACTIVÉ" : "Module revendeurs DÉSACTIVÉ");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTogglingResellers(false);
    }
  };

  const runTest = async (kind) => {
    const setter = kind === "wa" ? setTestingWa : setTestingEmail;
    const url = kind === "wa" ? "/admin/whatsapp-test" : "/admin/email-test";
    setter(true);
    try {
      const { data } = await api.post(url);
      toast.success(kind === "wa" ? `Message WhatsApp de test envoyé au ${data.masked || data.to}` : `Email de test envoyé à ${data.to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setter(false);
    }
  };

  const cards = [
    { label: "Chiffre d'affaires", value: formatPrice(stats?.revenue || 0, "XOF"), icon: Wallet, tone: "bg-brand text-white" },
    { label: "Commandes", value: stats?.orders_count ?? 0, icon: ShoppingBag, tone: "bg-white text-ink" },
    { label: "Panier moyen", value: formatPrice(stats?.average_order || 0, "XOF"), icon: TrendingUp, tone: "bg-white text-ink" },
    { label: "Clients", value: stats?.customers_count ?? 0, icon: Users, tone: "bg-white text-ink" },
    { label: "Produits actifs", value: `${stats?.active_products ?? 0}/${stats?.products_count ?? 0}`, icon: Package, tone: "bg-white text-ink" },
    { label: "Clés disponibles", value: stats?.licenses?.available ?? 0, icon: KeyRound, tone: "bg-white text-ink" },
  ];

  const maxDay = Math.max(...(stats?.revenue_by_day || []).map((d) => d.total), 1);

  return (
    <div className="space-y-5">
      {/* ---------------- Contrôles rapides : module revendeurs + tests notifications ---------------- */}
      <div className="grid gap-4 xl:grid-cols-2">
        <div
          data-testid="reseller-module-card"
          className={`rounded-2xl border p-5 transition-colors ${resellersOn ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${resellersOn ? "bg-emerald-500/15 text-emerald-600" : "bg-slate-300/40 text-slate-500"}`}>
                <Store className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-base font-extrabold text-ink">Module revendeurs</p>
                <p className="mt-0.5 max-w-md text-xs text-slate-500">
                  {resellersOn
                    ? "Activé : les boutiques, la page « Devenir revendeur » et l'espace vendeur sont accessibles."
                    : "Désactivé : les pages revendeur, le menu « Boutiques / Devenir revendeur » et les inscriptions revendeur sont masqués. Le site fonctionne uniquement pour vous."}
                </p>
                <p className="mt-1.5">
                  <Pill tone={resellersOn ? "green" : "rose"} data-testid="reseller-module-status">
                    {resellersOn ? "ACTIVÉ" : "DÉSACTIVÉ"}
                  </Pill>
                </p>
              </div>
            </div>
            <button
              type="button"
              data-testid="reseller-module-toggle"
              onClick={toggleResellers}
              disabled={togglingResellers}
              className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-xs font-black text-white shadow-lg transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${resellersOn ? "bg-rose-600 shadow-rose-600/30" : "bg-emerald-600 shadow-emerald-600/30"}`}
            >
              {togglingResellers ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
              {resellersOn ? "Désactiver" : "Activer"}
            </button>
          </div>
        </div>

        <div data-testid="notifications-test-card" className="rounded-2xl border border-line bg-white p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light text-brand">
              <MessageCircle className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <p className="font-display text-base font-extrabold text-ink">Tester les notifications de commande</p>
              <p className="mt-0.5 text-xs text-slate-500">Envoyez un message de test vers le WhatsApp et l'email de l'administrateur.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="test-whatsapp-btn"
                  onClick={() => runTest("wa")}
                  disabled={testingWa}
                  className="flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {testingWa ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />} Tester WhatsApp
                </button>
                <button
                  type="button"
                  data-testid="test-email-btn"
                  onClick={() => runTest("email")}
                  disabled={testingEmail}
                  className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-bold text-white hover:bg-brand disabled:opacity-60"
                >
                  {testingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Tester Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map(({ label, value, icon: Icon, tone }) => (
          <div key={label} className={`rounded-2xl border border-line p-4 ${tone}`} data-testid={`admin-stat-${label.replace(/[^a-zA-Z]/g, "")}`}>
            <Icon className="h-5 w-5 opacity-70" />
            <p className="mt-3 font-display text-lg font-extrabold leading-tight">{value}</p>
            <p className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <AdminCard title="Chiffre d'affaires (14 derniers jours)" description="Commandes payées">
          {stats?.revenue_by_day?.length ? (
            <div className="flex h-44 items-end justify-center gap-2">
              {stats.revenue_by_day.map((day) => (
                <div key={day.day} className="group flex max-w-[52px] flex-1 flex-col items-center gap-2">
                  <span className="text-[9px] font-bold text-slate-400 opacity-0 transition-opacity group-hover:opacity-100">
                    {Math.round(day.total / 1000)}k
                  </span>
                  <div
                    className="w-full rounded-t-md bg-brand transition-colors duration-200 group-hover:bg-brand-dark"
                    style={{ height: `${Math.max((day.total / maxDay) * 130, 6)}px` }}
                  />
                  <span className="text-[9px] text-slate-400">{day.day.slice(8)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-slate-500">Aucune vente enregistrée.</p>
          )}
        </AdminCard>

        <AdminCard title="État des licences" description="Répartition par statut">
          <div className="space-y-3">
            {[
              ["available", "Disponibles", "green"],
              ["reserved", "Réservées", "amber"],
              ["sold", "Vendues", "blue"],
              ["cancelled", "Annulées", "rose"],
            ].map(([key, label, tone]) => (
              <div key={key} className="flex items-center justify-between rounded-xl border border-line px-4 py-3">
                <Pill tone={tone}>{label}</Pill>
                <span className="font-display text-lg font-extrabold text-ink" data-testid={`admin-license-${key}`}>
                  {stats?.licenses?.[key] ?? 0}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {stats?.payments_to_verify > 0 && (
              <Link to="/admin/commandes" data-testid="dashboard-payments-alert" className="flex items-center gap-1.5 rounded-lg bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700">
                <Wallet className="h-3.5 w-3.5" /> 🔔 {stats.payments_to_verify} paiement(s) à vérifier
              </Link>
            )}
            {stats?.open_tickets > 0 && (
              <Link to="/admin/tickets" className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
                <MessagesSquare className="h-3.5 w-3.5" /> {stats.open_tickets} ticket(s) à traiter
              </Link>
            )}
            {stats?.licenses?.available < 20 && (
              <span className="flex items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700">
                <AlertCircle className="h-3.5 w-3.5" /> Stock de clés faible
              </span>
            )}
          </div>
        </AdminCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <AdminCard
          title="Dernières commandes"
          action={
            <Link to="/admin/commandes" className="text-xs font-bold text-brand hover:underline">
              Tout voir
            </Link>
          }
        >
          <TableWrap>
            <table className="w-full">
              <thead>
                <tr className="border-b border-line text-left">
                  <Th>Commande</Th>
                  <Th>Client</Th>
                  <Th>Paiement</Th>
                  <Th>Statut</Th>
                  <Th className="text-right">Total</Th>
                </tr>
              </thead>
              <tbody data-testid="admin-recent-orders">
                {(stats?.recent_orders || []).map((order) => (
                  <tr key={order.id} className="border-b border-line last:border-0">
                    <Td className="font-bold">{order.order_number}</Td>
                    <Td className="text-slate-500">{order.customer?.name}</Td>
                    <Td className="text-slate-500">{PAYMENT_LABELS[order.payment_method]}</Td>
                    <Td>
                      <Pill tone={order.status === "completed" ? "green" : "amber"}>
                        {ORDER_STATUS[order.status]?.label || order.status}
                      </Pill>
                    </Td>
                    <Td className="text-right font-bold">{formatPrice(order.total, "XOF")}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </AdminCard>

        <AdminCard title="Meilleures ventes">
          <div className="space-y-3">
            {(stats?.top_products || []).map((product, i) => (
              <div key={product.slug} className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-500">
                  {i + 1}
                </span>
                <img src={product.image_url} alt="" className="h-10 w-10 rounded-lg bg-slate-50 object-contain p-1" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{product.name}</span>
                  <span className="text-xs text-slate-500">{product.sales_count} ventes</span>
                </span>
                <span className="text-sm font-bold text-brand">{formatPrice(product.price, "XOF")}</span>
              </div>
            ))}
          </div>
        </AdminCard>
      </div>
    </div>
  );
}
